import {
    dataOrElse,
    Games,
    mapData,
    Preferences,
    Promotion,
    Tokens
} from '@nackle/arcade-core-v2';
import { setLanguageBundle, CURRENT_LOCALE_BUNDLE } from '@nackle/intl-tools';
import orderBy from 'lodash/fp/orderBy';
import React from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import RoomCabinetList from './arcade-room';
import GameBanner from './banner';
import InstantPlays from './instant-play';
import { fetchRoomNames } from './utils/roomNames';
import './ArcadeIndex.less';
import Header from '../app/components/arcade-header/Header';
import { appStore } from '../app-store';
import { loadCrate } from '@nackle/umbrella';
import CypherService from '../services/cypher-services';
import { ARCADE_PROJECT_STENCIL, CRATE_PROJECT_NAME, transformBundle } from '../utils/constants';
import translationCache from '../utils/cache';
import CenteredSpinner from '../components/CenteredSpinner';

const mapStateToProps = () => {
    const getNonEmptyNonFreePlayRooms = createSelector(
        ( state ) => Games.getRoomList( state ),
        ( rooms ) => mapData( rooms, ( roomList ) =>
            roomList.filter( ( room ) => !room.freePlay && room.cabinets.length ),
        ),
    );

    return ( state ) => {
        const arcades = Games.getArcadeList( state );
        const nonEmptyNonFreePlayRooms = getNonEmptyNonFreePlayRooms( state );
        const tokensLoaded = Tokens.getAvailableTokens( state ).isLoaded;
        const roomsTokens = nonEmptyNonFreePlayRooms.isLoaded && tokensLoaded && 
            ( nonEmptyNonFreePlayRooms.data.filter( ( room ) => !room.freePlay && room.cabinets.length ) ).map(
                room => {
                    const tokens = dataOrElse(
                        Tokens.countAvailableTokensForRoom( state, room.id ), 0
                    ) || 0;

                    return {
                        ...room,
                        tokens
                    };
                }
            );

        // Get all cabinets from Redux state (arcade-core convention: games.cabinetsById)
        const allCabinets = Games.getCabinetList( state );
        const romIds = [ ...new Set( ( allCabinets?.data || [] ).map( ( { romId } ) => romId ).filter( Boolean ), ), ];
        return {
            arcade: arcades,
            mainRoomId: arcades.isLoaded && arcades.data.length > 0 && arcades.data[ 0 ].mainRoomId,
            rooms: Games.getRoomList( state ),
            nonEmptyNonFreePlayRooms: roomsTokens,
            romIds,
            arcadesList: arcades && arcades.isLoaded ? arcades.data : [],
        };
    };
};

const mapDispatchToProps = {
    loadArcadesAndTheirData: Games.loadArcadesAndTheirData,
    loadPreferences: Preferences.loadPreferences,
    loadTokens: Tokens.loadTokens,
    pickARandomCabinetToBePromoted: Promotion.pickARandomCabinetToBePromoted,
};

class ArcadeIndex extends React.Component {
    state = {
        selectedCabinetId: undefined,
        selectedRoomId: undefined,
        loading: true,
        roomNamesMap: {},
    };

    lastRoomNamesRequestKey = null;

    async componentDidMount() {
        await this.loadArcadeData();
        await this.loadRoomNames();
        await this.ensureLanguageBundle();
    }
    async componentDidUpdate( prevProps ) {
        const { romIds, nonEmptyNonFreePlayRooms } = this.props;
        const prevRomIds = ( prevProps.romIds || [] ).join( ',' );
        const currentRomIds = romIds.join( ',' );
        const becameReady =
            !prevProps.nonEmptyNonFreePlayRooms && !!nonEmptyNonFreePlayRooms;

        if ( romIds.length > 0 && prevRomIds !== currentRomIds ) {
            await this.ensureLanguageBundle();
            await this.loadRoomNames();
        } else if ( becameReady ) {
            await this.loadRoomNames();
        }
    }

    // Cache for mergedBundle to avoid redundant API calls
    mergedBundleCache = null;
    resultTranslation = null;

    async ensureLanguageBundle() {
        // Check if CURRENT_LOCALE_BUNDLE is empty or missing keys
        try {
            const { romIds, umbrellaProps } = this.props;
            if ( romIds.length === 0 ) {return;}
            const locale = this.getLocale( umbrellaProps );
            if ( this.mergedBundleCache ) {
                // If we already have the merged bundle, just set it
                await setLanguageBundle( locale, this.mergedBundleCache );
                return;
            }
            if ( !CURRENT_LOCALE_BUNDLE || Object.keys( CURRENT_LOCALE_BUNDLE ).length === 0 ) {
                const user = this.getUser( umbrellaProps );
                const cypher = CypherService( user );
                const result = await cypher.getStencilInstancesByCodesAndLocales( ARCADE_PROJECT_STENCIL, locale, romIds );
                if ( result && result.stencils ) {
                    const transformedBundle = transformBundle( result, locale );
                     if ( umbrellaProps?.isUmbrella ) {
                      // Already in correct format
                      this.resultTranslation = await loadCrate( CRATE_PROJECT_NAME, locale );
                    } else {
                      const resultTranslation = await cypher.getCratesByStackCodesAndLocales(
                        CRATE_PROJECT_NAME,
                        locale
                      );
                      // Transform only for cypher response
                      this.resultTranslation = Object.values( resultTranslation?.crates || {} ).reduce( ( acc, crate ) => {
                        const localeData = crate?.[ locale ] || {};
                        return { ...acc, ...localeData };
                      }, {} );
                    }
                    const mergedBundle = {
                        ...( this.resultTranslation || {} ),
                        ...( transformedBundle || {} )
                    };
                    this.mergedBundleCache = mergedBundle;
                    translationCache.setMergedBundle( transformedBundle );
                    this.setState( { loading: false } );
                    await setLanguageBundle( locale, mergedBundle );
                }
            }
        } catch ( e ) {
            this.setState( { loading: false } );
            console.error( '[ArcadeIndex] ensureLanguageBundle error:', e );
        }
    }

    getUser( umbrellaProps ) {
        let user = umbrellaProps && umbrellaProps.user;
        if ( !user && appStore && typeof appStore.getState === 'function' ) {
            const state = appStore.getState();
            user = state && state.session && state.session.authState ? state.session.authState : undefined;
        }
        return user;
    }

    getLocale( umbrellaProps ) {
        let locale = umbrellaProps && umbrellaProps.locale;
        if ( !locale && appStore && typeof appStore.getState === 'function' ) {
            const state = appStore.getState();
            locale = state && state.session && state.session.locale.data;
        }
        return locale;
    }

    loadRoomNames = async () => {
        const { nonEmptyNonFreePlayRooms, umbrellaProps } = this.props;

        const user = this.getUser( umbrellaProps );
        const locale = this.getLocale( umbrellaProps );

        try {
            const roomNamesResult = await fetchRoomNames( {
                rooms: nonEmptyNonFreePlayRooms,
                user,
                locale,
                lastRequestKey: this.lastRoomNamesRequestKey,
            } );

            if ( !roomNamesResult ) {
                return;
            }

            this.lastRoomNamesRequestKey = roomNamesResult.requestKey;
            this.setState( { roomNamesMap: roomNamesResult.roomNamesMap } );
        } catch ( e ) {
            console.error( '[ArcadeIndex] loadRoomNames error:', e );
        }
    };
    
    render() {
        const { arcade, nonEmptyNonFreePlayRooms } = this.props;
        const { loading, roomNamesMap } = this.state;

        if ( loading ) {
            return (
                <CenteredSpinner />
            );
        }

        const rooms = nonEmptyNonFreePlayRooms && arcade.isLoaded &&
            orderBy( [ 'tokens', 'name' ], [ 'desc', 'asc' ] )( nonEmptyNonFreePlayRooms )
                .map( ( room ) => (
                    <RoomCabinetList
                        key={ room.id }
                        room={ room }
                        onClickCabinet={ this.handleClickCabinet }
                        isUmbrella={ this.props.umbrellaProps?.isUmbrella }
                        roomName={ roomNamesMap[ room.id ] } />
                ) );

        return (
            <div className="arcade-index">
                <Header />
                <GameBanner
                    isUmbrella={ this.props.umbrellaProps?.isUmbrella }
                />
                <InstantPlays
                    onClickCabinet={ this.handleClickCabinet }
                    isUmbrella={ this.props.umbrellaProps?.isUmbrella }
                    user={ this.getUser( this.props.umbrellaProps ) }
                    locale={ this.getLocale( this.props.umbrellaProps ) }
                />
                { rooms }
            </div>
        );
    }

    handleClickCabinet = ( roomId, cabinetId ) => {
        this.setState( {
            selectedCabinetId: cabinetId,
            selectedRoomId: roomId,
        } );
    };

    async loadArcadeData() {
        this.props.loadPreferences().catch( ( e ) => console.warn( e ) );

        await Promise.all( [
            this.props.loadArcadesAndTheirData(),
            this.props.loadTokens(),
        ] );

        // No duplicate translation fetching here; handled in ensureLanguageBundle
        this.props.pickARandomCabinetToBePromoted( {
            selectCabinetEntry: this.props.selectCabinetEntry,
            selectToken: this.props.selectToken,
        } );
    }
}

export default connect( mapStateToProps, mapDispatchToProps )( ArcadeIndex );
